import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MAIN_CONTENT_ID, ROUTE_CHROME_ATTRIBUTE } from '../constants/a11y';
import { formatDocumentTitle, getRouteTitle } from '../constants/routeTitles';

// How long a navigation keeps looking for the new page's <h1> (and, while the page is still
// empty, for any content at all) before it stops waiting. Pages fetch before they render
// their heading, so the heading is often not there yet when the route commits.
const HEADING_WAIT_MS = 2000;

// Attributes that can flip an already-mounted heading between hidden and rendered.
const VISIBILITY_ATTRIBUTES = ['style', 'hidden', 'aria-hidden', 'class'];

// A heading only counts if it is really rendered. React hides the previous page with
// display:none while a lazy route suspends, and that stale <h1> must not win. The walk
// stops at <main>: display:none is not inherited, so every ancestor has to be checked.
const isRendered = (element, main) => {
  for (let node = element; node && node !== main; node = node.parentElement) {
    if (node.hidden || node.getAttribute('aria-hidden') === 'true') return false;
    const { display, visibility } = window.getComputedStyle(node);
    if (display === 'none' || visibility === 'hidden') return false;
  }
  return true;
};

// The first rendered <h1> INSIDE <main>. The <h1>s in TopNavigation / SidebarNavigation
// sit outside it and are deliberately ignored.
const findHeading = main => Array.from(main.querySelectorAll('h1')).find(h1 => isRendered(h1, main));

// Elements that make a page "not empty" even when they carry no text of their own.
const CONTENT_SELECTOR = 'input, select, textarea, button, img, svg, canvas, video, iframe';

// Persistent chrome inside <main> (the trial / grace-period banners), marked in App.js. It is
// there before, during and after every navigation, so it says nothing about the new page.
const CHROME_SELECTOR = `[${ROUTE_CHROME_ATTRIBUTE}]`;
const isRouteChrome = element => Boolean(element.closest(CHROME_SELECTOR));

// Has the new page painted anything yet? Rendered text or a rendered control / image counts
// (a skeleton, a spinner label, a form). Nothing at all means a page that is about to
// redirect (<Navigate> renders null), an auth check, or a heading that is hidden until ready.
// Whitespace-only text, anything not rendered, and the route chrome do not count.
const hasRenderedContent = main => {
  const counts = element => !isRouteChrome(element) && isRendered(element, main);
  const walker = document.createTreeWalker(main, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.nodeValue.trim() && counts(node.parentElement)) return true;
  }
  return Array.from(main.querySelectorAll(CONTENT_SELECTOR)).some(counts);
};

// Moving focus is only polite while nobody else has claimed it. It is free when it is
// still where the navigation left it (`origin`: the element focused at the user's latest
// input, usually the clicked link), or on <body> / <main> itself. It is NOT free when the
// new page took it (an autofocused input) or focus moved without further input since: a
// Tab-away, or a programmatic focus(), even while a lazy chunk was still loading. (A click or
// Enter on ANOTHER control is itself an input and becomes the new origin; see
// useRouteAnnouncer.)
const focusIsFree = (main, origin) => {
  const active = document.activeElement;
  return !active || active === document.body || active === main || active === origin;
};

// Headings are not focusable by default. Give the heading a temporary tabindex="-1"
// (unless the page set its own) and take it off again on the first blur, so it does not
// become a permanent tab-order / click-target quirk.
const focusHeading = heading => {
  if (!heading.hasAttribute('tabindex')) {
    heading.setAttribute('tabindex', '-1');
    heading.addEventListener('blur', () => heading.removeAttribute('tabindex'), { once: true });
  }
  heading.focus();
};

// Settles one navigation: places focus and announces the route, deciding as the new page
// renders. After each DOM change inside <main>, in order:
//   a. focus is no longer free (the page or the user took it): leave it, just announce;
//   b. a rendered <h1> exists: focus it, announce;
//   c. no heading yet but the page has content: park focus on <main> right away and announce,
//      then keep watching, and move to the heading if one appears while focus is still on
//      <main> (a user or page that moved focus since is never overridden);
//   d. <main> is still empty (a <Navigate> hop, an auth check): wait. Nothing is announced
//      yet, so an intermediate redirect hop, which the next navigation cancels, never
//      reaches the live region. At HEADING_WAIT_MS focus <main> and announce.
// Watching ends at the first final outcome, or at HEADING_WAIT_MS. The returned cleanup
// cancels all of it (a newer navigation supersedes this one) and silences the announcement.
const focusWhenReady = (main, origin, announce) => {
  let observer = null;
  let timer = null;
  let finished = false;
  let parkedOnMain = false;

  const finish = () => {
    finished = true;
    if (observer) observer.disconnect();
    clearTimeout(timer);
  };

  const step = isLastChance => {
    if (finished) return;
    if (parkedOnMain) {
      if (document.activeElement !== main) return finish();
      const lateHeading = findHeading(main);
      if (lateHeading) {
        focusHeading(lateHeading);
        return finish();
      }
      return isLastChance ? finish() : undefined;
    }
    if (!focusIsFree(main, origin)) {
      announce();
      return finish();
    }
    const heading = findHeading(main);
    if (heading) {
      focusHeading(heading);
      announce();
      return finish();
    }
    if (hasRenderedContent(main)) {
      main.focus();
      announce();
      parkedOnMain = true;
      return isLastChance ? finish() : undefined;
    }
    if (isLastChance) {
      main.focus();
      announce();
      return finish();
    }
    return undefined;
  };

  step(false);
  if (!finished) {
    observer = new MutationObserver(() => step(false));
    observer.observe(main, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: VISIBILITY_ATTRIBUTES,
    });
    timer = setTimeout(() => step(true), HEADING_WAIT_MS);
  }
  // A superseded navigation: nothing it was waiting for may fire, focus or announce later.
  return finish;
};

// Real user input. Capture phase on `document`, so a handler that stops propagation cannot
// hide it; `click` is on the list because screen readers activate links with a synthetic
// click and no key or pointer event. Browser Back / Forward (`popstate`, on `window`) is
// input too: it is the user's own navigation, and it is never fired by a <Navigate> or
// pushState, so it cannot reopen the load-time-redirect hole.
const INPUT_EVENTS = ['keydown', 'pointerdown', 'click', 'touchstart'];

/**
 * Route-change accessibility for the SPA shell (WCAG 2.4.2, 2.4.3, 4.1.3). Mount it once,
 * in AppLayout. On every pathname change it
 *   - sets document.title from constants/routeTitles.js, immediately;
 *   - settles the navigation once the new page has rendered (see focusWhenReady): moves
 *     focus to the page's <h1> inside <main>, or to <main> when it has content but no
 *     heading, and hands the bare route title back as `announcement` for the polite live
 *     region at that same moment (never for a redirect hop that is cancelled before it
 *     settles).
 *
 * Only the title is set until the user has pressed, clicked, touched or gone Back. A
 * pathname change before that is the page still loading (a <Navigate> from `/` to the
 * dashboard, a signed-out deep link bounced to /login), not navigation: moving focus then
 * would put it past the skip link, so the first Tab would no longer reach it. The initial
 * pathname is remembered in a ref, so React StrictMode's double-invoked mount effect, and
 * query-string or hash changes (same pathname), never move focus or re-announce either.
 *
 * Focus is not taken from someone who already has it: the element focused at the latest
 * input is the origin, and focus is only moved while it is still there (or on <body> /
 * <main>). So a Tab-away or a programmatic focus() while a lazy chunk is still loading, and
 * an input the new page autofocuses, win over the heading. Known limit: a click or Enter on
 * ANOTHER control during that wait is itself an input, so that control becomes the origin
 * and the heading may take focus from it when the chunk lands.
 */
const useRouteAnnouncer = () => {
  const { pathname } = useLocation();
  const [announcement, setAnnouncement] = useState('');
  const previousPathname = useRef(pathname);
  const focusOrigin = useRef(null);
  const userHasActed = useRef(false);

  useEffect(() => {
    // Record where focus was at the moment of input (a keydown fires before Tab moves
    // focus; a click's target is already focused). Reading it at commit instead would be
    // too late: BrowserRouter commits in a transition, possibly seconds after the click, and
    // a Tab-away in between would then count as the origin. Every input re-snapshots, so the
    // origin is "focus at the LATEST input": Tab-away and programmatic focus moves are
    // protected, a click or Enter on another control in that gap is not.
    const markUserInput = () => {
      userHasActed.current = true;
      focusOrigin.current = document.activeElement;
    };
    INPUT_EVENTS.forEach(type => document.addEventListener(type, markUserInput, true));
    window.addEventListener('popstate', markUserInput, true);
    return () => {
      INPUT_EVENTS.forEach(type => document.removeEventListener(type, markUserInput, true));
      window.removeEventListener('popstate', markUserInput, true);
    };
  }, []);

  useEffect(() => {
    const title = getRouteTitle(pathname);
    document.title = formatDocumentTitle(title);

    if (previousPathname.current === pathname) return undefined;
    previousPathname.current = pathname;
    if (!userHasActed.current) return undefined;

    const main = document.getElementById(MAIN_CONTENT_ID);
    if (!main) {
      setAnnouncement(title);
      return undefined;
    }
    return focusWhenReady(main, focusOrigin.current, () => setAnnouncement(title));
  }, [pathname]);

  return { announcement };
};

export default useRouteAnnouncer;
